import React from "react";
import ServerManagerClient from "../services/server-manager.client";

import { bytesToGB, bytesToMB, CPU, FleetAgent, Memory, Network } from "../types/FleetAgent";
import { Col, Modal, Nav, Row, Tab } from "react-bootstrap";

// Register the components
import { Chart, registerables } from 'chart.js';
import { create } from "lodash";
Chart.register(...registerables);

interface FleetAgentProps {
  agent: FleetAgent | undefined;
  onClose: () => void;
}

const ViewFleetAgent = (props: FleetAgentProps) => {
  const sm = new ServerManagerClient();
  const networkChart = React.useRef<Chart | null>(null);
  const packetsChart = React.useRef<Chart | null>(null);
  const cpuChart = React.useRef<Chart | null>(null);
  const memoryChart = React.useRef<Chart | null>(null);

  const [ip, setIp] = React.useState<string>("");
  const [agent, setAgent] = React.useState<FleetAgent | undefined>(props.agent);
  const [network, setNetwork] = React.useState<Network[]>([]);
  const [cpu, setCpu] = React.useState<CPU[]>([]);
  const [memory, setMemory] = React.useState<Memory[]>([]);

  const handleClose = () => {
    setIp("");
    setAgent(undefined);
    setNetwork([]);
    setCpu([]);
    setMemory([]);
    
    if (networkChart.current) {
      networkChart.current.destroy();
      networkChart.current = null;
    }
    if (packetsChart.current) {
      packetsChart.current.destroy();
      packetsChart.current = null;
    }
    if (cpuChart.current) {
      cpuChart.current.destroy();
      cpuChart.current = null;
    }
    if (memoryChart.current) {
      memoryChart.current.destroy();
      memoryChart.current = null;
    }

    props.onClose();
  }

  React.useEffect(() => {
    createCharts();
  }, []);

  React.useEffect(() => {
    console.info("ViewFleetAgent", props.agent);

    if (props.agent) {
      if (props.agent.ip !== ip) {
        setIp(props.agent.ip);
        setAgent(props.agent);
        setNetwork([props.agent.network]);
        setCpu([props.agent.cpu]);
        setMemory([props.agent.memory]);

        createCharts();
        

      } else {
        createCharts();

        const data = [...network, props.agent.network].slice(-1000);
        setNetwork(data);
        const cpuData = [...cpu, props.agent.cpu].slice(-1000);
        setCpu(cpuData);
        const memoryData = [...memory, props.agent.memory].slice(-1000);
        setMemory(memoryData);
      }
    }
  }, [props.agent]);

  const createCharts = () => {
    const ctx = document.getElementById("networkChart") as HTMLCanvasElement;
    if (ctx && !networkChart.current) {
      networkChart.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: network.map((_, index) => index.toString()),
          datasets: [
            {
              label: "Data Received (MB)",
              data: network.map((net) => bytesToMB(net.bytesRecv)),
              borderColor: "rgba(75, 192, 192, 1)",
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
            {
              label: "Data Sent (MB)",
              data: network.map((net) => bytesToMB(net.bytesSent)),
              borderColor: "rgba(192, 75, 192, 1)",
              backgroundColor: "rgba(192, 75, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'white', // Adjust the color as needed
              },
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Time',
                color: 'white', // Adjust the color as needed
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'MB',
                color: 'white', // Adjust the color as needed
              },
            },
          },
        },
      });
    }

    const packetsCanvas = document.getElementById("packetsChart") as HTMLCanvasElement;
    if (packetsCanvas && !packetsChart.current) {
      packetsChart.current = new Chart(packetsCanvas, {
        type: "line",
        data: {
          labels: network.map((_, index) => index.toString()),
          datasets: [
            {
              label: "Packets Recieved",
              data: network.map((net) => net.packetsRecv),
              borderColor: "rgba(75, 192, 192, 1)",
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
            {
              label: "Packets Sent",
              data: network.map((net) => net.packetsSent),
              borderColor: "rgba(192, 75, 192, 1)",
              backgroundColor: "rgba(192, 75, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'white', // Adjust the color as needed
              },
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Time',
                color: 'white', // Adjust the color as needed
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: '# Packets',
                color: 'white', // Adjust the color as needed
              },
            },
          },
        },
      });
    }

    const cpuCanvas = document.getElementById("cpuChart") as HTMLCanvasElement;
    if (cpuCanvas && !cpuChart.current) {
      cpuChart.current = new Chart(cpuCanvas, {
        type: "line",
        data: {
          labels: cpu.map((_, index) => index.toString()),
          datasets: [
            {
              label: "CPU Usage",
              data: cpu.map((net) => net.usage),
              borderColor: "rgba(75, 192, 192, 1)",
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'white', // Adjust the color as needed
              },
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Time',
                color: 'white', // Adjust the color as needed
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: '% Utilisation',
                color: 'white', // Adjust the color as needed
              },
            },
          },
        },
      });
    }

    const memoryCanvas = document.getElementById("memoryChart") as HTMLCanvasElement;
    if (memoryCanvas && !memoryChart.current) {
      memoryChart.current = new Chart(memoryCanvas, {
        type: "line",
        data: {
          labels: memory.map((_, index) => index.toString()),
          datasets: [
            {
              label: "Memory Usage",
              data: memory.map((net) => net.used),
              borderColor: "rgba(75, 192, 192, 1)",
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            },
            {
              label: "Memory Total",
              data: memory.map((net) => net.total),
              borderColor: "rgba(192, 75, 192, 1)",
              backgroundColor: "rgba(192, 75, 192, 0.2)",
              borderWidth: 1,
              fill: true,
            }
          ],
        },
        options: {
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'white', // Adjust the color as needed
              },
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Time',
                color: 'white', // Adjust the color as needed
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'GB used',
                color: 'white', // Adjust the color as needed
              },
            },
          },
        },
      });
    }
  };

  React.useEffect(() => {
    if (networkChart.current) {
      networkChart.current.data.labels = network.map((_, index) => index.toString());
      networkChart.current.data.datasets[0].data = network.map((net) => bytesToMB(net.bytesRecv));
      networkChart.current.data.datasets[1].data = network.map((net) => bytesToMB(net.bytesSent));
      networkChart.current.update();
    }

    if (packetsChart.current) {
      packetsChart.current.data.labels = network.map((_, index) => index.toString());
      packetsChart.current.data.datasets[0].data = network.map((net) => net.packetsRecv);
      packetsChart.current.data.datasets[1].data = network.map((net) => net.packetsSent);
      packetsChart.current.update();
    }

    if (cpuChart.current) {
      cpuChart.current.data.labels = cpu.map((_, index) => index.toString());
      cpuChart.current.data.datasets[0].data = cpu.map((net) => net.usage);
      cpuChart.current.update();
    }

    if (memoryChart.current) {
      memoryChart.current.data.labels = memory.map((_, index) => index.toString());
      memoryChart.current.data.datasets[0].data = memory.map((net) => bytesToGB(net.used));
      memoryChart.current.update();
    }
  }, [network]);

  return (
    <>
      <Modal show={agent !== undefined} onHide={() => handleClose()} className="modal-dark">
        <Modal.Header closeButton className="modal-header-dark">
          <Modal.Title>{agent?.name} - {agent?.ip}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-dark">
          <Tab.Container defaultActiveKey="graph">

            <Nav variant="pills" className="flex">
              <Nav.Item>
                <Nav.Link eventKey="graph">Graphs</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="servers">Servers</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="graph">
                <Row>
                  <Col>
                    <h3>Network Data</h3>
                    <canvas id="networkChart" width="100" height="100"></canvas>
                  </Col>
                  <Col>
                    <h3>Network Packets</h3>
                    <canvas id="packetsChart" width="100" height="100"></canvas>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h3>CPU Usage</h3>
                    <canvas id="cpuChart" width="100" height="100"></canvas>
                  </Col>
                  <Col>
                    <h3>Memory Usage</h3>
                    <canvas id="memoryChart" width="100" height="100"></canvas>
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="servers">
                <table className="table table-dark table-striped">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Status</th>
                      <th>Game</th>
                      <th>Size</th>
                      <th>Name</th>
                      <th>Version</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agent?.servers.map((server) => (
                      <tr key={server.id}>
                        <td>{server.id}</td>
                        <td>{server.status}</td>
                        <td>{server.game}</td>
                        <td>{server.size}</td>
                        <td>{server.name}</td>
                        <td>{server.version}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tab.Pane>

            </Tab.Content>
          </Tab.Container>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewFleetAgent;